import React, { FC } from 'react';
import { withRouter, Redirect, RouteComponentProps } from 'react-router';

import { GetGroupComponent, Group as GroupType } from 'api';
import * as paths from 'paths';
import { Layouts, QueryManager } from 'components';
import { FeedSelect } from 'features/feed';
import { useCurrentUser, useFollowed } from 'hooks';

import { IMatchParams } from '../types';
import { canModerateGroupContent } from '../helpers';

import GroupMembers from '../components/GroupMembers';
import GroupPosts from '../components/GroupPosts/GroupPostsIndex';
import GroupApproval from '../components/GroupApproval';
import GroupRemoved from '../components/GroupRemoved';

interface IPropsEnhanced extends RouteComponentProps<IMatchParams> {}

const Group: FC<IPropsEnhanced> = props => {
  const currentUser = useCurrentUser();
  const { groups } = useFollowed();
  const { match } = props;

  const isPosts = match.path === paths.group;
  const isMembers = match.path === paths.groupMembers;
  const isApproval = match.path === paths.groupApproval;
  const isRemoved = match.path === paths.groupRemoved;

  const { group_id } = match.params;
  const isFollowing = groups.some(group => group.id === group_id);

  function canModerate(group: GroupType) {
    return canModerateGroupContent(currentUser, group.id);
  }

  function renderGroup(group: GroupType) {
    if (isPosts) {
      return <GroupPosts isFollowing={isFollowing} group={group} />;
    }

    if (isMembers) {
      return <GroupMembers isFollowing={isFollowing} group={group} />;
    }

    if (canModerate(group) && isApproval) {
      return <GroupApproval group={group} />;
    }

    if (canModerate(group) && isRemoved) {
      return <GroupRemoved group={group} />;
    }

    return <Redirect to={paths.groups} />;
  }

  return (
    <Layouts.App2Column
      sidebar={<FeedSelect />}
      main={
        <GetGroupComponent variables={{ id: group_id }}>
          {queryResult => (
            <QueryManager {...queryResult}>
              {({ data }) => {
                if (!data || !data.getGroup) {
                  return <Redirect to={paths.groups} />;
                }

                const { getGroup } = data;
                return renderGroup(getGroup);
              }}
            </QueryManager>
          )}
        </GetGroupComponent>
      }
    />
  );
};

export default withRouter(Group);
